import axios from 'axios';
import config from '../Config';
import {
  getStoredToken,
  handleUnauthorizedSession,
  requestSessionRefresh,
} from '../authSession';

class UnauthorizedError extends Error {
  constructor() {
    super('Unauthorized');
    this.name = 'UnauthorizedError';
    this.isUnauthorized = true;
  }
}

function createClient({ withAuthRetry }) {
  const client = axios.create({
    baseURL: config.hostApi,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    validateStatus: () => true,
  });

  client.interceptors.request.use((requestConfig) => {
    const nextConfig = { ...requestConfig };

    if (!nextConfig.skipAuth) {
      const token = getStoredToken();

      if (token) {
        nextConfig.headers = {
          ...nextConfig.headers,
          Authorization: `Bearer ${token}`,
        };
      }
    }

    return nextConfig;
  });

  if (withAuthRetry) {
    let refreshPromise = null;

    client.interceptors.response.use(async (response) => {
      const originalRequest = response.config || {};

      if (response.status !== 401 || originalRequest.skipAuth) {
        return response;
      }

      if (originalRequest._retry || !getStoredToken()) {
        handleUnauthorizedSession();
        throw new UnauthorizedError();
      }

      originalRequest._retry = true;

      try {
        if (refreshPromise === null) {
          refreshPromise = requestSessionRefresh().finally(() => {
            refreshPromise = null;
          });
        }

        await refreshPromise;

        return client(originalRequest);
      } catch (error) {
        handleUnauthorizedSession();
        throw new UnauthorizedError();
      }
    });
  }

  return client;
}

function buildResponse(response) {
  const payload = response.data;

  return {
    ok: response.status >= 200 && response.status < 300,
    status: response.status,
    statusText: response.statusText,
    headers: response.headers,
    data: payload,
    json: async () => payload,
    text: async () => (typeof payload === 'string' ? payload : JSON.stringify(payload)),
  };
}

export const apiClient = createClient({ withAuthRetry: true });
export const authClient = createClient({ withAuthRetry: false });

export function request(config) {
  return apiClient(config)
    .then(buildResponse)
    .catch((error) => {
      if (error && error.isUnauthorized) {
        return new Promise(() => {});
      }

      throw error;
    });
}

export function requestWithoutRetry(config) {
  return authClient(config).then(buildResponse);
}

export function get(url, config = {}) {
  return request({
    ...config,
    method: 'GET',
    url,
  });
}

export function post(url, data, config = {}) {
  return request({
    ...config,
    method: 'POST',
    url,
    data,
  });
}

export function put(url, data, config = {}) {
  return request({
    ...config,
    method: 'PUT',
    url,
    data,
  });
}

export function destroy(url, config = {}) {
  return request({
    ...config,
    method: 'DELETE',
    url,
  });
}

export function getWithoutRetry(url, config = {}) {
  return requestWithoutRetry({
    ...config,
    method: 'GET',
    url,
  });
}

export function postWithoutRetry(url, data, config = {}) {
  return requestWithoutRetry({
    ...config,
    method: 'POST',
    url,
    data,
  });
}
